import { inject, Injectable } from '@angular/core';
import {
	catchError,
	EMPTY,
	forkJoin,
	map,
	Observable,
	of,
	switchMap,
	tap
} from 'rxjs';
import {
	FilterFieldMap,
	Image,
	Reseller,
	ResellerDTO,
	UserDto
} from '../models';
import { DialogService, ApiService } from '../../shared/services';
import { Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { FILE_UPLOAD_IMAGE_INPUTS } from '../models/file-upload-image-inputs.model';
import { RESELLER_FORM } from '../models/form/reseller-form';
import { PayloadRequest, PayloadResponse } from '../models/payload.model';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { trimFormValues } from '../../shared/utils';

@Injectable({
	providedIn: 'root'
})
export class ResellerService {
	private apiService = inject(ApiService);
	private dialogService = inject(DialogService);

	constructor(
		private router: Router,
		private fb: FormBuilder
	) {}

	createResellerFormGroup(): FormGroup {
		const formBuilder = new FormBuilder();
		const formControls: { [key: string]: FormControl } = {};

		Object.keys(RESELLER_FORM).forEach(key => {
			const field = RESELLER_FORM[key];
			formControls[key] = new FormControl(field.value, field.validators);
		});

		return formBuilder.group(formControls);
	}

	createImageResellerFormGroup(): FormGroup {
		return this.fb.group({
			logoDark: this.fb.control(''),
			logoLight: this.fb.control(''),
			favicon: this.fb.control('')
		});
	}

	loadResellerData(id: number, form: FormGroup): Observable<any> {
		return this.getResellerById(id).pipe(
			tap(reseller => {
				form.patchValue(reseller);
			})
		);
	}

	navigateToList(): void {
		this.router.navigate(['/main/rivenditori']);
	}

	getUploadImageInputs(): Observable<any> {
		return of(FILE_UPLOAD_IMAGE_INPUTS);
	}

	getResellerFormFields(): Observable<any> {
		return of(RESELLER_FORM);
	}

	getResellersList(): Observable<PayloadResponse<Reseller[]>>;
	getResellersList(
		filter: FilterFieldMap,
		pageNumber: number,
		pageSize: number
	): Observable<PayloadResponse<Reseller[]>>;
	getResellersList(
		filter?: FilterFieldMap,
		pageNumber?: number,
		pageSize?: number
	): Observable<PayloadResponse<Reseller[]>> {
		let params = new HttpParams();
		if (filter) {
			if (filter['company'].value)
				params = params.set('company', filter['company'].value);
			if (filter['pIvaTaxId'])
				params = params.set('pIvaTaxId', filter['pIvaTaxId'].value);
			if (filter['email'].value)
				params = params.set('email', filter['email'].value);
			if (filter['isActive'].value)
				params = params.set('isActive', filter['isActive'].value);
		}

		if (pageNumber) params = params.set('pageNumber', pageNumber);
		if (pageSize) params = params.set('pageSize', pageSize);

		return this.apiService.get('resellers', { params: params });
	}

	countItems(): Observable<{ activeItems: number; inactiveItems: number }> {
		return this.apiService
			.get('resellers/count-status')
			.pipe(map(payload => payload.data));
	}

	getResellerById(resellerId: number): Observable<PayloadResponse<Reseller>> {
		return this.apiService.get(`resellers/${resellerId}`).pipe(
			map(reseller => {
				return {
					...reseller.data,
					nationId: reseller.data.nation?.id ?? null,
					certifyingAgencyId: reseller.data.certifyingAgency?.id ?? null,
					cityId: reseller.data.city?.id ?? null,
					referentBirthPlaceId: reseller.data.referentBirthPlace?.id ?? null,
					isActive: reseller.data.user?.isActive ?? false
				};
			})
		);
	}

	getResellerByUserId(userId: number): Observable<Reseller> {
		return this.apiService.get(`resellers/user/${userId}`).pipe(
			map((reseller: PayloadResponse<Reseller>) => {
				return {
					...reseller.data,
					nationId: reseller.data.nation?.id ?? null,
					certifyingAgencyId: reseller.data.certifyingAgency.id,
					isActive: reseller.data.user ? reseller.data.user.isActive : false
				};
			})
		);
	}

	getResellerByUserUrl(
		resellerUrl: string
	): Observable<PayloadResponse<string>> {
		return this.apiService.get(`resellers/url/${resellerUrl}`);
	}

	onSubmitForm(
		resellerId: number,
		formReseller: FormGroup
	): Observable<PayloadResponse<Reseller>> {
		trimFormValues(formReseller);

		if (formReseller.invalid) {
			formReseller.markAllAsTouched();

			this.dialogService.openDialogInfoNoRedirect(
				'Errore',
				'Compila tutti i campi obbligatori.'
			);

			return of();
		}

		const isUpdate = !!resellerId;
		const resellerDTO = this.convertTypeFormReseller(formReseller);
		const observable = isUpdate
			? this.updateReseller(resellerDTO)
			: this.saveReseller(resellerDTO);

		return observable.pipe(
			catchError(err => {
				return EMPTY;
			}),
			switchMap(res => {
				const id = res.data.resellerId;
				if (!id) return of(res);

				const imageFields = [
					'logoDark',
					'logoLight',
					'favicon',
					'logoAzienda',
					'firmaAttestato',
					'imgFooter'
				];

				const uploadCalls = imageFields
					.map(fieldName => {
						const file = formReseller.get(fieldName)?.value;
						if (
							file instanceof File &&
							file.size > 0 &&
							file.name !== 'existing-image.jpg'
						) {
							return this.uploadImage(id, fieldName, file);
						}
						return null;
					})
					.filter((obs): obs is Observable<any> => !!obs);

				// `null` is the sentinel set by the upload box when the user removes
				// an image that was already saved server-side: the actual deletion
				// only happens here, at submit time.
				const deleteCalls = imageFields
					.map(fieldName => {
						const value = formReseller.get(fieldName)?.value;
						if (value === null) {
							return this.deleteImage(id, fieldName);
						}
						return null;
					})
					.filter((obs): obs is Observable<any> => !!obs);

				const imageCalls = [...uploadCalls, ...deleteCalls];

				return imageCalls.length > 0
					? forkJoin(imageCalls).pipe(map(() => res))
					: of(res);
			}),
			tap(() => this.showDialogSuccess(isUpdate))
		);
	}

	private saveReseller(
		resellerDto: ResellerDTO
	): Observable<PayloadResponse<Reseller>> {
		const payload = new PayloadRequest<ResellerDTO>({ data: resellerDto });
		return this.apiService.post('resellers/create', payload);
	}

	private updateReseller(
		resellerDto: ResellerDTO
	): Observable<PayloadResponse<Reseller>> {
		const payload = new PayloadRequest<ResellerDTO>({ data: resellerDto });
		return this.apiService.patch(`resellers/update`, payload);
	}

	private showDialogSuccess(isUpdate: boolean): void {
		const dialogMessage = isUpdate
			? 'Il rivenditore è stato modificato con successo'
			: 'Il rivenditore è stato creato con successo, é stato inviato il link di attivazione alla mail del rivenditore ';

		this.dialogService.openDialogAlert(
			'Operazione completata',
			dialogMessage,
			() => {
				this.router.navigate(['/main/rivenditori']);
			}
		);
	}

	toggleStatusReseller(reseller: Reseller): Observable<void> {
		const userDto: UserDto = { username: reseller.email };
		const payload = new PayloadRequest<UserDto>({ data: userDto });
		return this.apiService.patch(`user/update-status`, payload);
	}

	convertTypeFormReseller(form: FormGroup): ResellerDTO {
		const value = form.value;

		const trimOrNull = (val: any): string | null => {
			return typeof val === 'string' ? val.trim() || null : null;
		};

		const reseller: ResellerDTO = {
			resellerId: value.resellerId,
			userId: Number(value.userId),
			nationId: value.nationId > 0 ? Number(value.nationId) : null,
			certifyingAgencyId: Number(value.certifyingAgencyId),
			isActive: value.isActive === true || value.isActive === 'true',
			company: value.company?.trim(),
			address: trimOrNull(value.address),
			cityId: value.cityId > 0 ? Number(value.cityId) : null,
			postalCode: trimOrNull(value.postalCode),
			pIvaTaxId: trimOrNull(value.pIvaTaxId),
			phoneNumber: trimOrNull(value.phoneNumber),
			fax: trimOrNull(value.fax),
			url: value.url?.trim(),
			website: trimOrNull(value.website),
			email: value.email?.trim(),
			abbreviation: value.abbreviation?.trim(),
			isAutomaticRelease:
				value.isAutomaticRelease === true ||
				value.isAutomaticRelease === 'true',
			extraNotes: trimOrNull(value.extraNotes),
			brandColor: value.brandColor?.trim(),
			referentName: value.referentName?.trim(),
			referentSurname: value.referentSurname?.trim(),
			referentEmail: value.referentEmail?.trim(),
			referentFiscalCode: trimOrNull(value.referentFiscalCode),
			referentBirthDate: value.referentBirthDate
				? new Date(value.referentBirthDate)
				: null,
			referentBirthPlaceId:
				value.referentBirthPlaceId > 0
					? Number(value.referentBirthPlaceId)
					: null,
			referentPostalCode: trimOrNull(value.referentPostalCode),
			htmlCode: trimOrNull(value.htmlCode)
		};

		return reseller;
	}

	// Export data resellers

	downloadResellerCsv(filter: FilterFieldMap): Observable<any> {
		let params = this.setParams(filter);
		return this.apiService.get('resellers/reseller-csv', {
			params: params,
			responseType: 'blob',
			observe: 'response'
		});
	}

	private setParams(filter: FilterFieldMap): HttpParams {
		let params = new HttpParams();
		if (filter) {
			if (filter['company'].value)
				params = params.set('company', filter['company'].value);
			if (filter['pIvaTaxId'].value)
				params = params.set('pIvaTaxId', filter['pIvaTaxId'].value);
			if (filter['email'].value)
				params = params.set('email', filter['email'].value);
			if (filter['isActive'].value)
				params = params.set('isActive', filter['isActive'].value);
		}
		return params;
	}

	// IMAGES

	getImageBlobByResellerIdAndType(resellerId: number, imgType: string) {
		let params = new HttpParams();
		params = params.set('resellerId', resellerId);
		params = params.set('imgType', imgType);
		return this.apiService.get(`images/blob`, {
			params: params,
			responseType: 'blob',
			observe: 'response'
		});
	}

	getImageBlobByResellerUrlAndType(resellerUrl: string, imgType: string) {
		let params = new HttpParams();
		params = params.set('resellerUrl', resellerUrl);
		params = params.set('imgType', imgType);
		return this.apiService.get(`images/blob-reseller-url`, {
			params: params,
			responseType: 'blob',
			observe: 'response'
		});
	}

	getImageByResellerIdAndType(
		resellerId: number,
		imgType: string
	): Observable<PayloadResponse<Image>> {
		let params = new HttpParams();
		params = params.set('resellerId', resellerId);
		params = params.set('imgType', imgType);
		return this.apiService.get(`images/image`, { params: params });
	}

	uploadImage(resellerId: number, imgType: string, file: File) {
		const formData = new FormData();
		formData.append('resellerId', resellerId.toString());
		formData.append('imageType', imgType);
		formData.append('imageFile', file);
		return this.apiService.post(`images/upload`, formData);
	}

	deleteImage(resellerId: number, imgType: string) {
		let params = new HttpParams();
		params = params.set('resellerId', resellerId);
		params = params.set('imgType', imgType);
		return this.apiService.delete(`images/delete`, {
			headers: new HttpHeaders(),
			params: params
		});
	}
}
